// ui/DisabledHint/DisabledHint.tsx — the shared "greyed control with an accessible
// explanation" pattern (slice-4-xl: Upload video, and later Go Live / the live-chat
// composer / player chrome). A disabled control that only LOOKS inert is the same
// dead-control problem this batch keeps fixing — so the explanation is always in the
// DOM and wired via aria-describedby, not conjured only on hover.
//
// CONTRACT for every consumer (documented here once — Live inherits it):
//   - Use `aria-disabled="true"`, NEVER the native `disabled` HTML attribute. A
//     native-disabled element is unfocusable, so this component's onFocus (the
//     keyboard path to the tooltip) would never fire — a real bug this fixed
//     (Upload was keyboard-unreachable before this contract existed).
//   - Stay focusable (don't set tabIndex={-1}); most native controls (button,
//     link) are already in the tab order once `disabled` is dropped.
//   - Suppress the click instead of wiring a real handler:
//     `onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}`. The
//     stopPropagation matters: preventDefault alone cancels nothing on a
//     type="button"/role="button" control, and without it the click bubbles into
//     ancestor handlers — e.g. a "disabled" play glyph inside a <Link> card would
//     still navigate.
//   - Style the disabled look off `[aria-disabled="true"]` (Button already does
//     this — see ui/Button).
//
// Sizing: by default the wrapper shrink-wraps its child (inline-flex), which is
// correct for a button or a small glyph. Pass `block` when the child must fill an
// already-sized ancestor (e.g. a full-width composer) — the default is NOT safe to
// wrap around a sized/absolutely-positioned layout, since an inline-flex box with
// only absolutely-positioned content collapses to 0×0 (a real bug this fixed: the
// video player's thumb was invisible because DisabledHint wrapped the whole player
// instead of just the play glyph — see VideoDetailPage/ShortsPlayerPage for the
// corrected structure: DisabledHint wraps ONLY the glyph, nested inside the sized,
// already-positioned player frame).
//
// Usage:
//   <DisabledHint explanation="Video features aren't available yet.">
//     {(describedBy) => (
//       <Button aria-disabled="true" aria-describedby={describedBy} onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
//         Upload video
//       </Button>
//     )}
//   </DisabledHint>

import { useId, useState, type ReactNode } from 'react';
import styled from 'styled-components';

const Wrap = styled.span<{ $block: boolean }>`
  position: relative;
  display: ${({ $block }) => ($block ? 'block' : 'inline-flex')};
  ${({ $block }) => ($block ? 'width: 100%;' : '')}
`;

const Bubble = styled.span<{ $visible: boolean }>`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 30;
  white-space: nowrap;
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
  color: ${({ theme }) => theme.color.textPrimary};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  box-shadow: ${({ theme }) => theme.elevation.md};
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  transition: opacity 120ms ease;
`;

export interface DisabledHintProps {
  /** The accessible explanation — always present in the DOM, shown on hover/focus. */
  explanation: string;
  /** Render-prop so the caller wires `aria-describedby={describedBy}` onto its own control. */
  children: (describedBy: string) => ReactNode;
  /**
   * Set true when the child must fill its parent (e.g. a full-width composer)
   * instead of shrink-wrapping. See the sizing note above before reaching for
   * this — usually the fix is to wrap a smaller glyph/control, not to stretch
   * DisabledHint around a whole layout.
   */
  block?: boolean;
}

export function DisabledHint({ explanation, children, block = false }: DisabledHintProps) {
  const id = useId();
  const [visible, setVisible] = useState(false);

  return (
    <Wrap
      $block={block}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
      onFocus={() => setVisible(true)}
      onBlur={() => setVisible(false)}
    >
      {children(id)}
      <Bubble role="tooltip" id={id} $visible={visible}>
        {explanation}
      </Bubble>
    </Wrap>
  );
}
